import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Dropdown, Label, Separator } from '@heroui/react';
import { Navbar } from '@heroui-pro/react';
import { AppWindowIcon, SignOutIcon } from '@phosphor-icons/react';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { useLogout, logout as logoutAction } from '@hs/shared';
import { trpc } from '@/lib/trpc';
import logoFullUrl from '@/assets/images/logo-full.png';

/**
 * Header 组件
 * 
 * 功能：
 * 1. 左侧显示 Logo；顶部 sticky 固定 + 磨砂透明
 * 2. 右侧显示主导航（定价 / 指南 / 联络），与登录区用 Separator 分隔
 * 3. 右侧根据登录状态显示：
 *    - 未登录：显示"登录"、"注册"按钮
 *    - 已登录：显示 AvatarMenu 组件
 */

const NAV_ITEMS = [
  { label: '定价', href: '/products' },
  { label: '指南', href: '/help' },
  { label: '联络', href: '/contact' },
] as const;

function getUserDisplayName(user: { nickname?: string; username: string }): string {
  return user.nickname || user.username;
}

function getUserInitials(user: { nickname?: string; username: string }): string {
  const displayName = getUserDisplayName(user);
  if (/[\u4e00-\u9fa5]/.test(displayName)) {
    return displayName.charAt(0).toUpperCase();
  }
  return displayName.substring(0, 2).toUpperCase();
}

export function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);

  const { logout: handleLogout, isLoggingOut } = useLogout({
    trpc,
    dispatch,
    logout: logoutAction,
  });

  const handleGoToApp = () => {
    const appUrl = import.meta.env.VITE_APP_URL || 'http://app.hs.test:5175';
    window.open(appUrl, '_blank');
  };

  const displayName = user ? getUserDisplayName(user) : '';
  const initials = user ? getUserInitials(user) : '';

  return (
    <Navbar
      className="h-16 border-b border-default-200/60 bg-white/70 backdrop-blur-md backdrop-saturate-150"
      position="sticky"
      shouldBlockScroll={false}
      navigate={(href) => navigate(href)}
    >
      <Navbar.Header className="container mx-auto h-full w-full px-4">
        <Navbar.Brand className="mr-2">
          <Link to="/" className="flex h-full items-center">
            <img src={logoFullUrl} alt="人文派" className="h-6 w-auto" />
          </Link>
        </Navbar.Brand>

        <Navbar.Spacer />

        <Navbar.Content className="items-center gap-3">
          <div className="hidden items-center gap-1 sm:flex">
            {NAV_ITEMS.map((item) => (
              <Navbar.Item
                key={item.href}
                href={item.href}
                isCurrent={pathname === item.href}
                className="text-lg"
              >
                {item.label}
              </Navbar.Item>
            ))}
          </div>

          <Navbar.Separator className="hidden sm:block" />

          {isAuthenticated && user ? (
            <Dropdown>
              <Button isIconOnly aria-label="用户菜单" variant="ghost" className="rounded-full">
                <Avatar className="size-9">
                  {user.avatarUrl ? <Avatar.Image alt={displayName} src={user.avatarUrl} /> : null}
                  <Avatar.Fallback>{initials}</Avatar.Fallback>
                </Avatar>
              </Button>

              <Dropdown.Popover placement="bottom end" className="min-w-[220px]">
                <div className="px-3 pt-3 pb-1">
                  <div className="flex items-center gap-2">
                    <Avatar className="size-8">
                      {user.avatarUrl ? <Avatar.Image alt={displayName} src={user.avatarUrl} /> : null}
                      <Avatar.Fallback>{initials}</Avatar.Fallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium">{displayName}</span>
                      {user.email ? (
                        <span className="truncate text-xs text-default-500">{user.email}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <Dropdown.Menu aria-label="用户操作">
                  <Separator />
                  <Dropdown.Item id="go-app" textValue="进入应用" onAction={handleGoToApp}>
                    <AppWindowIcon className="size-4 text-default-500" />
                    <Label>进入应用</Label>
                  </Dropdown.Item>
                  <Dropdown.Item
                    id="logout"
                    textValue="退出登录"
                    onAction={handleLogout}
                    isDisabled={isLoggingOut}
                    className="text-danger"
                  >
                    <SignOutIcon className="size-4 text-danger" />
                    <Label>退出登录</Label>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          ) : (
            <>
              <Button variant="ghost" onPress={() => navigate('/login')}>
                登录
              </Button>
              <Button variant="primary" onPress={() => navigate('/register')}>
                立即体验
              </Button>
            </>
          )}
          <Navbar.MenuToggle className="sm:hidden" />
        </Navbar.Content>
      </Navbar.Header>

      <Navbar.Menu className="sm:hidden">
        {NAV_ITEMS.map((item) => (
          <Navbar.MenuItem key={item.href} href={item.href} isCurrent={pathname === item.href}>
            {item.label}
          </Navbar.MenuItem>
        ))}
      </Navbar.Menu>
    </Navbar>
  );
}

